import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'

const TimeContext = createContext(null)

export function TimeProvider({ children }) {
  const [simulatedTime, setSimulatedTime] = useState(null)
  const [_tick, setTick] = useState(0)
  const simulationStartRef = useRef(null)
  const simulationBaseRef = useRef(null)

  // Tick every 60 seconds so consumers re-render for time-based UI
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 60000)
    return () => clearInterval(interval)
  }, [])

  const now = useCallback(() => {
    if (simulatedTime) {
      // Advance simulated time by the real elapsed time since simulation started
      const elapsed = Date.now() - simulationStartRef.current
      return new Date(simulationBaseRef.current + elapsed)
    }
    return new Date()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- _tick forces callback invalidation every 60s so consumers re-render for time-based UI
  }, [simulatedTime, _tick])

  const setTime = useCallback((date) => {
    if (date === null) {
      setSimulatedTime(null)
      simulationStartRef.current = null
      simulationBaseRef.current = null
    } else {
      const target = date instanceof Date ? date : new Date(date)
      simulationStartRef.current = Date.now()
      simulationBaseRef.current = target.getTime()
      setSimulatedTime(target)
    }
  }, [])

  const isSimulating = simulatedTime !== null

  return (
    <TimeContext.Provider value={{ now, setTime, isSimulating }}>
      {children}
    </TimeContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- useTime hook is co-located with its provider by convention
export function useTime() {
  const ctx = useContext(TimeContext)
  if (!ctx) {
    // Fallback if used outside provider
    return { now: () => new Date(), setTime: () => {}, isSimulating: false }
  }
  return ctx
}
